import arrowUpSrc from "@/assets/svg/animation/arrow-up.svg"
import ChevronDownSrc from "@/assets/svg/animation/chevron-down.svg"
import WebBackgroundSrc from "@/assets/svg/animation/web-background.svg"
import anime from "animejs"
import { AnimeTimelineInstance } from "animejs"
import React, { FC, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"

import { Chat, IChatScene } from "./Chat"
import { getChatImage } from "./animationHelpers"

// Animation: Framing and Surrounding UI
const WebWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`
const WebDevice = styled.div`
  /* stylelint-disable-next-line */
  --frame-roundedness: 16px;
  --device-ui-height: 54px;

  position: relative;
  display: flex;
  flex-flow: column;
  width: 100%;
  max-width: 300px;
  height: 100%;
  max-height: 480px;
  margin: 0 auto;
  user-select: none;
  background: white;
  border-radius: var(--frame-roundedness);
  box-shadow: 0 4px 55px -3px rgba(173, 159, 255, 0.2);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
    content: "";
    border-radius: var(--frame-roundedness);
    box-shadow: inset 0 0 0 1px rgba(236, 244, 255, 0.8);
  }
`
const Header = styled.div`
  position: relative;
  display: flex;
  gap: 0 6px;
  align-items: center;
  width: 100%;
  height: var(--device-ui-height);
  padding: 6px 20px 0;
  background: #12122a;
`
const HeaderImage = styled.img`
  background: white;
  border-radius: 33px;
`
const VendorName = styled.span`
  margin-left: 6px;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  color: white;
`
const Content = styled.div<any>`
  position: relative;
  display: flex;
  flex-flow: column;
  gap: 6px 0;
  height: 100%;
  padding: 0 16px 16px 16px;
  overflow: hidden;
  background: #060816;

  > div {
    opacity: 0;
    transition: opacity 300ms ease;

    ${({ loaded }) =>
      loaded &&
      css`
        opacity: 1;
      `}
  }
`
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--device-ui-height);
  padding: 10px;
  background: #12122a;
`
const FooterInput = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding-left: 16px;
  font-size: 12px;
  line-height: 19px;
  color: white;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 50px;

  &::after {
    display: block;
    width: 28px;
    height: 28px;
    margin-right: 12px;
    content: "";
    background: url(${arrowUpSrc}) center right no-repeat;
  }
`
const WebBackground = styled.div`
  position: absolute;
  top: 40%;
  left: 7%;
  z-index: -1;
  width: 100%;
  max-width: 448px;
  height: 100%;
  max-height: 297px;
  background: url("${WebBackgroundSrc}") center center no-repeat;
`
const WebToggler = styled.div`
  position: absolute;
  bottom: -48px;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;

  &::before {
    display: block;
    width: 38px;
    height: 38px;
    margin: 12px 0 0 auto;
    content: "";
    background: rgba(255, 255, 255, 0.2) url("${ChevronDownSrc}") center
      no-repeat;
    border-radius: 38px;
  }
`

export const Web: FC<IChatScene> = (props) => {
  const { identity } = props

  // Refs
  const DeviceRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<AnimeTimelineInstance>()
  const contentContainerRef = useRef<HTMLDivElement>(null)

  // State
  const [isLoaded, setIsLoaded] = useState(false) // Start up the timeline and fade the component (avoids a FOUCing mess)
  const [enableChat, setEnableChat] = useState(false)

  // Handle effects
  useEffect(() => {
    setIsLoaded(true)

    // Handle Anime.js timeline
    timelineRef.current = anime.timeline({
      autoplay: false,
      easing: "easeOutCubic",
      loop: false,
    })

    // Handle timeline animations
    timelineRef.current
      .add({
        complete: () => {
          // Initialize this scene
          setEnableChat(true)
          // Emit an event to start the chat at the same time (otherwise it's already going)
          window.dispatchEvent(new CustomEvent("pxmStartChatAnim"))
          timelineRef.current?.pause()
        },
        delay: 150,
        duration: 600,
        opacity: "1",
        targets: DeviceRef.current,
      })
      .add({
        complete: () => {
          // Trigger a scene handoff on AnimationPlayer
          setEnableChat(false)
          window.dispatchEvent(new CustomEvent("pxmPreTransitionSceneComplete"))
        },
        delay: 10,
        duration: 180,
        opacity: [1, 0],
        scale: [1, 0.9],
        targets: DeviceRef.current,
      })

    // Initialize scene
    setEnableChat(true)
    timelineRef.current?.play()

    // Timeline resume when web chat is done
    const resumeTimeline = () => {
      timelineRef.current?.play()
    }

    // Event listeners
    //pxmPreTransitionSceneComplete
    window.addEventListener("pxmWebChatAnimComplete", resumeTimeline)

    // Cleanup
    return () => {
      // Remove event listeners
      window.removeEventListener("pxmWebChatAnimComplete", resumeTimeline)

      // Clear timeline
      timelineRef.current?.pause()
      timelineRef.current = undefined
    }
  }, [])

  return (
    <WebWrapper ref={DeviceRef}>
      <WebDevice>
        <Header>
          <HeaderImage
            alt={identity.chatTitle}
            height="33"
            src={getChatImage(identity.chatImage)}
            width="33"
          />
          <VendorName>{identity.chatTitle}</VendorName>
        </Header>
        <Content loaded={isLoaded} ref={contentContainerRef}>
          {enableChat && (
            <Chat
              {...props}
              containerRef={contentContainerRef.current ?? <></>}
              key="sms-chat"
            />
          )}
        </Content>
        <Footer>
          <FooterInput>Write a reply</FooterInput>
        </Footer>
        <WebToggler />
      </WebDevice>
      <WebBackground />
    </WebWrapper>
  )
}
